// =============================================================================
// iTravel 2.0 — Sync Store (Zustand)
// =============================================================================
// Manages offline sync state and bridges the SyncEngine to the React UI.
// This store exposes the current SyncState (network status, pending mutations,
// sync progress) so that components like SyncStatusIndicator can reactively
// display the current offline/online status.
//
// The store also provides actions to:
//   - Initialize and tear down the sync engine
//   - Manually trigger a sync
//   - Queue offline mutations
//   - Clear sync data
//
// Usage:
//   const { syncState, isInitialized } = useSyncStore();
//   const { initialize, syncNow, queueMutation } = useSyncStore();
// =============================================================================

import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { SyncAction, SyncEntityType, SyncState } from '@itravel/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SyncStoreState {
  /** Whether the sync engine has been initialized */
  isInitialized: boolean;

  /** Whether initialization is currently in progress */
  isInitializing: boolean;

  /** Current sync state exposed to the UI */
  syncState: SyncState;

  /** Error from initialization, if any */
  initError: string | null;
}

export interface SyncStoreActions {
  /** Initialize the offline database and start the sync engine */
  initialize: (userId: string) => Promise<void>;

  /** Shut down the sync engine and clean up listeners */
  teardown: () => Promise<void>;

  /** Manually trigger a sync (e.g., when user taps "Sync now") */
  syncNow: () => Promise<void>;

  /**
   * Queue a mutation for offline sync.
   * Call this when a write operation fails due to network issues,
   * or proactively when you know the device is offline.
   */
  queueMutation: (params: {
    entityType: SyncEntityType;
    entityId: string;
    action: SyncAction;
    payload: Record<string, unknown>;
  }) => Promise<void>;

  /** Clear all cached data and sync queue (e.g., on logout) */
  /**
   * Destroys this account's cache AND its queue of unsent changes. Only for
   * an explicit "wipe my data on this device" or account deletion — logging
   * out must NOT call it, or a queue built up offline dies with the session.
   */
  clearAll: () => Promise<void>;

  /** Update the sync state (called internally by the sync engine listener) */
  _updateSyncState: (partial: Partial<SyncState>) => void;

  /** The API answered: online, and the engine is told so it can sync. */
  _markReachable: () => void;
}

type SyncStore = SyncStoreState & SyncStoreActions;

// ---------------------------------------------------------------------------
// Default sync state
// ---------------------------------------------------------------------------

const DEFAULT_SYNC_STATE: SyncState = {
  network: {
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    connectionType: 'unknown',
  },
  isSyncing: false,
  pendingCount: 0,
  failedCount: 0,
  lastSyncedAt: undefined,
  lastError: undefined,
};

// ---------------------------------------------------------------------------
// Lazy engine/db imports to avoid circular dependencies and keep the store
// lightweight until initialization is actually requested.
// ---------------------------------------------------------------------------

let _syncEngine: Awaited<typeof import('@/lib/sync/sync-engine')>['syncEngine'] | null = null;
let _offlineDb: Awaited<typeof import('@/lib/db/offline-db')>['offlineDb'] | null = null;
let _unsubscribe: (() => void) | null = null;

async function getSyncEngine() {
  if (!_syncEngine) {
    const mod = await import('@/lib/sync/sync-engine');
    _syncEngine = mod.syncEngine;
  }
  return _syncEngine;
}

async function getOfflineDb() {
  if (!_offlineDb) {
    const mod = await import('@/lib/db/offline-db');
    _offlineDb = mod.offlineDb;
  }
  return _offlineDb;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSyncStore = create<SyncStore>()(
  devtools(
    (set, get) => ({
      // -- State --
      isInitialized: false,
      isInitializing: false,
      syncState: DEFAULT_SYNC_STATE,
      initError: null,

      // -- Actions --

      initialize: async (userId: string) => {
        const { isInitialized, isInitializing } = get();
        if (isInitialized || isInitializing) return;

        set({ isInitializing: true, initError: null });

        try {
          // 1. Open this account's database. Each account has its own file, so
          //    one person's cache and outbox are never visible to another.
          const db = await getOfflineDb();
          await db.openForUser(userId);

          // 2. Get the sync engine and subscribe to state changes
          const engine = await getSyncEngine();

          _unsubscribe = engine.onStatusChange((engineState) => {
            // Map the engine's internal state shape to our SyncState.
            // The engine's NetworkState is 'online' | 'offline' | 'unknown',
            // so we map it to our SyncNetworkState connectionType accordingly.
            const isOnline = engineState.networkState !== 'offline';
            const connectionType =
              engineState.networkState === 'online'
                ? ('wifi' as const)
                : engineState.networkState === 'offline'
                  ? ('none' as const)
                  : ('unknown' as const);

            set({
              syncState: {
                network: {
                  isOnline,
                  connectionType,
                  lastOnlineAt: isOnline
                    ? new Date().toISOString()
                    : get().syncState.network.lastOnlineAt,
                },
                isSyncing: engineState.isSyncing,
                pendingCount: engineState.pendingCount,
                // Refused by the server, not merely unsent — these are waiting
                // on the user, not on connectivity.
                failedCount: engineState.deadCount,
                lastSyncedAt: engineState.lastSyncAt ?? undefined,
                lastError: engineState.lastError ?? undefined,
              },
            });
          });

          // 3. Start the sync engine (sets up network listeners, periodic sync)
          await engine.start();

          // 4. Read initial state from the engine
          const initialEngineState = engine.getState();
          const initialIsOnline = initialEngineState.networkState !== 'offline';
          const initialConnectionType =
            initialEngineState.networkState === 'online'
              ? ('wifi' as const)
              : initialEngineState.networkState === 'offline'
                ? ('none' as const)
                : ('unknown' as const);

          set({
            isInitialized: true,
            isInitializing: false,
            syncState: {
              network: {
                isOnline: initialIsOnline,
                connectionType: initialConnectionType,
              },
              isSyncing: initialEngineState.isSyncing,
              pendingCount: initialEngineState.pendingCount,
              failedCount: initialEngineState.deadCount,
              lastSyncedAt: initialEngineState.lastSyncAt ?? undefined,
              lastError: initialEngineState.lastError ?? undefined,
            },
          });

          console.info('[SyncStore] Initialized successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to initialize sync';
          console.error('[SyncStore] Initialization failed:', message);
          set({
            isInitializing: false,
            initError: message,
          });
        }
      },

      teardown: async () => {
        try {
          // Unsubscribe from engine state changes
          if (_unsubscribe) {
            _unsubscribe();
            _unsubscribe = null;
          }

          // Stop the sync engine
          if (_syncEngine) {
            await _syncEngine.stop();
          }

          // Close the offline database
          if (_offlineDb) {
            await _offlineDb.close();
          }

          _syncEngine = null;
          _offlineDb = null;

          set({
            isInitialized: false,
            isInitializing: false,
            syncState: DEFAULT_SYNC_STATE,
            initError: null,
          });

          console.info('[SyncStore] Torn down successfully');
        } catch (error) {
          console.error('[SyncStore] Teardown error:', error);
        }
      },

      syncNow: async () => {
        const { isInitialized } = get();
        if (!isInitialized) {
          console.warn('[SyncStore] Cannot sync — not initialized');
          return;
        }

        try {
          const engine = await getSyncEngine();
          await engine.syncNow();
        } catch (error) {
          console.error('[SyncStore] Manual sync failed:', error);
        }
      },

      queueMutation: async ({ entityType, entityId, action, payload }) => {
        try {
          const engine = await getSyncEngine();
          await engine.queueMutation({
            id: uuidv4(),
            entityType,
            entityId,
            action,
            payload,
          });

          // Update pending count immediately for responsive UI
          set((state) => ({
            syncState: {
              ...state.syncState,
              pendingCount: state.syncState.pendingCount + 1,
            },
          }));
        } catch (error) {
          console.error('[SyncStore] Failed to queue mutation:', error);
        }
      },

      clearAll: async () => {
        try {
          const db = await getOfflineDb();
          await db.clearAll();

          set((state) => ({
            syncState: {
              ...state.syncState,
              pendingCount: 0,
              failedCount: 0,
              lastError: undefined,
            },
          }));

          console.info('[SyncStore] All offline data cleared');
        } catch (error) {
          console.error('[SyncStore] Failed to clear offline data:', error);
        }
      },

      _updateSyncState: (partial) => {
        set((state) => ({
          syncState: {
            ...state.syncState,
            ...partial,
          },
        }));
      },

      _markReachable: () => {
        if (!get().isInitialized || get().syncState.network.isOnline) return;
        void getSyncEngine().then((engine) => engine.markReachable());
      },
    }),
    {
      name: 'sync-store',
      enabled: import.meta.env.DEV,
    },
  ),
);

// ---------------------------------------------------------------------------
// Selectors (for performance — prevents unnecessary re-renders)
// ---------------------------------------------------------------------------

export const selectSyncState = (state: SyncStore) => state.syncState;
export const selectIsOnline = (state: SyncStore) => state.syncState.network.isOnline;
export const selectIsSyncing = (state: SyncStore) => state.syncState.isSyncing;
export const selectPendingCount = (state: SyncStore) => state.syncState.pendingCount;
export const selectIsInitialized = (state: SyncStore) => state.isInitialized;
export const selectHasPendingChanges = (state: SyncStore) => state.syncState.pendingCount > 0;

// ---------------------------------------------------------------------------
// Standalone accessors (for use outside React components)
// ---------------------------------------------------------------------------

/**
 * Check if the device is currently online.
 * Can be called outside React (e.g., in API interceptors, sync engine).
 */
export function getIsOnline(): boolean {
  return useSyncStore.getState().syncState.network.isOnline;
}

/**
 * Check if there are pending offline mutations.
 */
export function getPendingCount(): number {
  return useSyncStore.getState().syncState.pendingCount;
}

/**
 * Get the full current sync state.
 */
export function getSyncState(): SyncState {
  return useSyncStore.getState().syncState;
}

export default useSyncStore;
